
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function SmartHomeSetupConfigurationPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Smart Home System Setup & Configuration',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Professional smart home system setup and configuration in Fresno, CA. Complete installation, device integration, automation programming, and user training.',
    url: `${siteUrl}/services/smart-home-energy/smart-home-setup-configuration`,
  };

  return (
    <>
      <SEOHead
        title="Smart Home Setup Fresno CA | Professional Configuration & Installation"
        description="Professional smart home system setup and configuration in Fresno, CA. Complete installation, device integration, automation programming, and training. Expert electrician service."
        canonical="/services/smart-home-energy/smart-home-setup-configuration"
        keywords="smart home setup Fresno, smart home configuration, home automation installation, smart home electrician"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Smart Home Setup & Configuration — Fresno, CA"
        heroSubtitle="Professional installation and configuration for seamless smart home operation"
        heroImage="https://public.readdy.ai/ai/img_res/7f0eb8ea288f2168ca8a774b20f44ab6.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Smart%20home%20configuration%20dashboard%20on%20tablet%20showing%20integrated%20devices%20automation%20rules%20and%20system%20settings%20with%20various%20smart%20home%20products%20visible%20in%20contemporary%20interior%20no%20people%20clean%20design&width=700&height=600&seq=smarthomesetup-fresno-01&orientation=portrait"
        fresnoContent="Fresno homeowners often buy smart home devices but struggle with setup, integration, and optimization. Our professional smart home setup service takes the frustration out of smart home technology. We install, configure, and integrate all your devices into one seamless system, create automated routines tailored to Fresno's climate, and train you on everything. Whether you're starting from scratch or need help with existing devices, we serve all Fresno neighborhoods including Woodward Park, Fig Garden, Tower District, and surrounding areas with expert smart home setup that ensures everything works perfectly together."
        features={[
          { icon: 'ri-home-gear-line', title: 'Complete System Setup', description: 'Install and configure all smart devices' },
          { icon: 'ri-links-line', title: 'Device Integration', description: 'Connect everything into unified system' },
          { icon: 'ri-calendar-check-line', title: 'Automation Programming', description: 'Create custom routines and schedules' },
          { icon: 'ri-wifi-line', title: 'Network Optimization', description: 'Ensure reliable connectivity for all devices' },
          { icon: 'ri-voice-recognition-line', title: 'Voice Assistant Setup', description: 'Configure Alexa, Google, and Siri' },
          { icon: 'ri-user-settings-line', title: 'User Training', description: 'Learn to use and customize your system' },
        ]}
        whatsIncluded={[
          'Complete smart home consultation and planning',
          'Wi-Fi network assessment and optimization',
          'Smart hub installation and configuration',
          'All device installation (lights, thermostats, locks, cameras)',
          'Device integration into unified system',
          'Voice assistant setup (Alexa, Google, Siri)',
          'Automated routine creation (Good Morning, Away, Night)',
          'Mobile app setup and user account creation',
          'Comprehensive user training on all features',
          'Ongoing support and troubleshooting',
        ]}
        faqs={[
          {
            question: "What's included in professional smart home setup in Fresno?",
            answer:
              "Our comprehensive setup includes: consultation to understand your needs, Wi-Fi network optimization, installation of all smart devices (lights, switches, thermostats, locks, cameras, sensors), integration into a unified system, voice assistant configuration, automated routine creation tailored to Fresno's climate, mobile app setup, and complete training. We handle everything from unboxing devices to teaching you advanced features. You'll have a fully functional, optimized smart home when we're done.",
          },
          {
            question: "Can you help with smart devices I've already purchased in Fresno?",
            answer:
              "Absolutely! Many Fresno homeowners buy smart devices but struggle with setup and integration. We'll install, configure, and integrate your existing devices, troubleshoot any issues, and optimize everything for best performance. If you have devices that don't work well together, we'll recommend solutions or compatible replacements. We also identify gaps in your system and suggest additions that would enhance functionality. Our goal is making your existing investment work perfectly.",
          },
          {
            question: 'How long does complete smart home setup take in Fresno?',
            answer:
              'Basic smart home setup (hub, 10-15 smart lights, thermostat, and voice assistant) takes 4-6 hours. Comprehensive setup with lighting, climate, security, and entertainment integration takes 1-2 full days for a typical Fresno home. Whole‑home automation with 30+ devices, multiple rooms, and complex automation takes 3-5 days. We work efficiently, test everything thoroughly, and ensure you\'re comfortable using your system before we leave. Most homeowners can use their home normally during installation.',
          },
          {
            question: 'What if I want to add more devices later to my Fresno smart home?',
            answer:
              "That's the beauty of professional setup—we design systems for easy expansion. Add new lights, sensors, cameras, or appliances anytime, and they'll integrate seamlessly with your existing system. We provide ongoing support for adding devices, and many Fresno homeowners start with basic automation and gradually expand. We document your system configuration so future additions are straightforward. You can add devices yourself or call us for professional installation and integration.",
          },
          {
            question: 'Do you provide training on using my smart home system in Fresno?',
            answer:
              'Yes! Comprehensive training is included with every setup. We teach you how to control devices manually and through apps, create and modify automated routines, use voice commands effectively, troubleshoot common issues, and customize settings. We provide written guides specific to your system and are available for follow‑up questions. Many Fresno homeowners schedule a follow‑up training session after they\'ve used their system for a few weeks to learn advanced features.',
          },
          {
            question: 'How much does professional smart home setup cost in Fresno?',
            answer:
              "Professional setup costs vary based on system complexity. Basic setup (hub, 10-15 devices, voice assistant) costs $500‑$800. Comprehensive setup with lighting, climate, security, and automation costs $1,200‑$2,000. Whole‑home automation with 30+ devices and complex integration costs $2,500‑$4,000. While DIY setup is possible, professional installation ensures everything works perfectly, saves you hours of frustration, and optimizes your system for Fresno's climate. Most homeowners find the investment worthwhile for the time saved and peace of mind.",
          },
        ]}
        ctaText="Get Professional Smart Home Setup"
      />
    </>
  );
}
